import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { requestGeoAddress } from "@/lib/geoAddress";

export interface TodayAttendanceEmployee {
  user_id: number;
  first_name: string;
  last_name: string | null;
  record_date: string;
  first_punchin: string;
  last_punchout: string | null;
  duration_seconds: number | null;
  in_latitude: string | number | null;
  in_longitude: string | number | null;
  out_latitude: string | number | null;
  out_longitude: string | number | null;
  in_geo_address: string | null;
  out_geo_address: string | null;
  in_selfie_id: number | null;
  out_selfie_id: number | null;
}

export interface TodayAttendanceCounts {
  totalActiveEmployees: number;
  punchedIn: number;
  punchedInAndOut: number;
  notPunched: number;
}

interface TodayAttendanceState {
  date: string | null;
  counts: TodayAttendanceCounts | null;
  employees: TodayAttendanceEmployee[];
  loading: boolean;
  error: string | null;
  geoLoading: Record<string, boolean>;
  geoError: Record<string, string | null>;
}

const initialState: TodayAttendanceState = {
  date: null,
  counts: null,
  employees: [],
  loading: false,
  error: null,
  geoLoading: {},
  geoError: {},
};

export const fetchTodayAttendanceDashboard = createAsyncThunk(
  "todayAttendance/fetchDashboard",
  async (date: string | undefined, { rejectWithValue }) => {
    const query = date ? `?date=${date}` : "";
    const res = await fetch(`/api/attendance/today-dashboard${query}`);
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to fetch today's attendance");
    return {
      date: data.date as string,
      counts: data.counts as TodayAttendanceCounts,
      employees: data.employees as TodayAttendanceEmployee[],
    };
  }
);

export const fetchGeoAddress = createAsyncThunk(
  "todayAttendance/fetchGeoAddress",
  async (
    { date, userId, type }: { date: string; userId: number; type: "in" | "out" },
    { rejectWithValue }
  ) => {
    try {
      const address = await requestGeoAddress({ userId, date, type });
      return { address, date, userId, type };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : "Failed to fetch address");
    }
  }
);

const todayAttendanceSlice = createSlice({
  name: "todayAttendance",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchTodayAttendanceDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTodayAttendanceDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.date = action.payload.date;
        state.counts = action.payload.counts;
        state.employees = action.payload.employees;
      })
      .addCase(fetchTodayAttendanceDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch today's attendance";
      })
      .addCase(fetchGeoAddress.pending, (state, action) => {
        const { date, userId, type } = action.meta.arg;
        const key = `${date}_${userId}_${type}`;
        state.geoLoading[key] = true;
        state.geoError[key] = null;
      })
      .addCase(fetchGeoAddress.fulfilled, (state, action) => {
        const { address, date, userId, type } = action.payload;
        const key = `${date}_${userId}_${type}`;
        state.geoLoading[key] = false;
        const employee = state.employees.find((e) => e.user_id === userId);
        if (employee) {
          if (type === "in") employee.in_geo_address = address;
          else employee.out_geo_address = address;
        }
      })
      .addCase(fetchGeoAddress.rejected, (state, action) => {
        const { date, userId, type } = action.meta.arg;
        const key = `${date}_${userId}_${type}`;
        state.geoLoading[key] = false;
        state.geoError[key] = (action.payload as string) ?? "Failed to fetch address";
      });
  },
});

export default todayAttendanceSlice.reducer;
